import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCompleteOnboarding } from './useCompleteOnboarding';
import { useAuthStore } from '@/core/stores/auth.store';
import { ONBOARDING_STEPS } from '../constants';
import { ORGANIZATIONS_KEYS } from '../../hooks/queryKeys';
import { organizationsService } from '../../api/organizations.service';
import type { OnboardingAnswers } from '../types';
import { useTranslation } from 'react-i18next';

const INITIAL_ANSWERS: OnboardingAnswers = {
  usage: [],
  manage: [],
  orgName: '',
  features: [],
  taskOrganization: [],
  inviteEmails: [],
};

export function useOnboarding() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { mutateAsync: completeOnboarding, isPending, error } = useCompleteOnboarding();

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<OnboardingAnswers>(INITIAL_ANSWERS);

  const totalSteps = ONBOARDING_STEPS.length;
  const step = ONBOARDING_STEPS[currentStep];
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;
  const progress = (currentStep / (totalSteps - 1)) * 100;
  const stepTitle = t(`steps.${step.id}`);

  const setAnswer = <K extends keyof OnboardingAnswers>(stepId: K, value: OnboardingAnswers[K]) => {
    setAnswers((prev) => ({ ...prev, [stepId]: value }));
  };

  const canGoNext = (): boolean => {
    switch (step.id) {
      case 'usage':
        return answers.usage.length > 0;
      case 'manage':
        return answers.manage.length > 0;
      case 'orgName':
        return answers.orgName.trim().length > 0;
      case 'features':
        return answers.features.length > 0;
      case 'taskOrganization':
        return answers.taskOrganization.length > 0;
      case 'inviteMembers':
        return true;
      default:
        return true;
    }
  };

  const goNext = async () => {
    if (!canGoNext()) return;

    if (isLastStep) {
      try {
        await completeOnboarding({
          organizationName: answers.orgName,
          answers: [
            { questionId: 'q1', optionIds: answers.usage },
            { questionId: 'q2', optionIds: answers.manage },
            { questionId: 'q3', optionIds: answers.features },
            { questionId: 'q4', optionIds: answers.taskOrganization },
          ],
          inviteeEmails: answers.inviteEmails,
        });
        const orgs = await queryClient.fetchQuery({
          queryKey: ORGANIZATIONS_KEYS.all,
          queryFn: ({ signal }) => organizationsService.getAll(signal),
        });
        if (orgs[0]) {
          navigate(`/${orgs[0].id}/members`, { replace: true });
        }
      } catch {
        // handled by global error handler
      }
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  const goBack = () => {
    if (!isFirstStep) setCurrentStep((prev) => prev - 1);
  };

  const skipStep = () => {
    if (step.optional) setCurrentStep((prev) => prev + 1);
  };

  return {
    currentStep,
    totalSteps,
    step,
    answers,
    progress,
    isPending,
    isError: !!error,
    error: error as Error | null,
    isFirstStep,
    isLastStep,
    canGoNext: canGoNext(),
    user,
    stepTitle,

    setAnswer,
    goNext,
    goBack,
    skipStep,
  };
}
